import { ImageManipulator, SaveFormat } from "expo-image-manipulator";
import { ForwardedRef, useCallback, useImperativeHandle } from "react";
import { SharedValue } from "react-native-reanimated";
import { consoleDevPerformanceDiff } from "src/helpers/devHelpers";
import { ImageCropperHandler } from "../../../types";

const useImageCropperHandler = ({
	uri,
	cropSV,
	parentRef,
	image,
	inside,
}: {
	uri: string;
	cropSV: SharedValue<{ x: number; y: number; scale: number }>;
	parentRef: ForwardedRef<ImageCropperHandler>;
	image: {
		width: number;
		height: number;
	};
	inside: {
		width: number;
		height: number;
	};
}) => {
	const getSelectedArea = useCallback<ImageCropperHandler["getSelectedArea"]>(() => {
		const size = Math.min(inside.width, inside.height);
		const imageSize = Math.min(image.width, image.height);
		const sizeRatio = imageSize / size;
		const relCrop = {
			x: cropSV.value.x * sizeRatio,
			y: cropSV.value.y * sizeRatio,
			scale: cropSV.value.scale,
		};
		const xDiff = (image.width - imageSize) / 2;
		const yDiff = (image.height - imageSize) / 2;

		const width = imageSize / relCrop.scale;
		const height = imageSize / relCrop.scale;
		return {
			x: imageSize / 2 - relCrop.x - width / 2 + xDiff,
			y: imageSize / 2 - relCrop.y - height / 2 + yDiff,
			width: imageSize / relCrop.scale,
			height: imageSize / relCrop.scale,
			imageWidth: image.width,
			imageHeight: image.height,
		};
	}, [cropSV, inside, image]);
	const getCroppedImage = useCallback<ImageCropperHandler["getCroppedImage"]>(async () => {
		const cropArea = getSelectedArea();
		const t = performance.now();
		const result = await (
			await ImageManipulator.manipulate(uri)
				.crop({
					originX: cropArea.x,
					originY: cropArea.y,
					width: cropArea.width,
					height: cropArea.height,
				})
				.renderAsync()
		).saveAsync({
			format: SaveFormat.PNG,
		});
		consoleDevPerformanceDiff("Cropped Image", t);
		return result;
	}, [getSelectedArea, uri]);
	useImperativeHandle(parentRef, () => ({
		getSelectedArea,
		getCroppedImage,
	}));
};
export default useImageCropperHandler;
